import { snapRoundDecimal } from '@calculations/round/decimal';
import { PayloadAction } from '@reduxjs/toolkit';
import { PriceStrategyConfigWithRelations } from '@snap-types/models/price-strategy-config';
import { dec } from '@snap-types/types/decimal';
import { WritableDraft } from 'immer';
import { z } from 'zod/v4';

import { QuoteBuilderStateType } from '../../types/state';
import { EditableQuoteBuilderOperationField } from '../../types/state/operation';
import { onPartQuantityChange } from '../calculations/recalculate';
import { buildElementId } from '../utilities/element-id';
import { buildIssueId } from '../utilities/issue-id';
import { deleteIssue, upsertIssue } from '../utilities/issue-managers';

export type OperationValueChangedParams = {
  quoteLineUuid: string;
  snapOperationUuid: string;
  field: EditableQuoteBuilderOperationField;
  value: string;
  priceStrategyConfigs: PriceStrategyConfigWithRelations[];
};

const validator = z.coerce
  .number({
    error: 'Operation value is required',
  })
  .gte(0, { message: 'Operation value must be greater than or equal to 0' });

export function operationValueChanged(
  state: WritableDraft<QuoteBuilderStateType>,
  action: PayloadAction<OperationValueChangedParams>,
) {
  const {
    value,
    quoteLineUuid,
    snapOperationUuid,
    field,
    priceStrategyConfigs,
  } = action.payload;

  const elementId = buildElementId({
    uuid: snapOperationUuid,
    field,
  });

  state.rawFieldValues[elementId] = value;

  const result = validator.safeParse(value);
  const invalidIssueId = buildIssueId({
    area: 'snapOperation',
    quoteLineUuid,
    snapOperationUuid,
    field,
    type: 'invalid',
  });

  if (!result.success) {
    return upsertIssue(state, invalidIssueId, {
      title: 'Invalid Field',
      description: z.flattenError(result.error).formErrors[0],
      severity: 'error',
      area: 'snapOperation',
      quoteLineUuid,
      snapOperationUuid,
      type: 'invalid',
      action: {
        type: 'modify',
        elementId,
      },
    });
  }

  deleteIssue(state, invalidIssueId);

  const decimalValue = snapRoundDecimal(dec(result.data));

  const snapWorkOrder = state.quoteLines[quoteLineUuid].snapWorkOrder;

  if (!snapWorkOrder) {
    return;
  }

  snapWorkOrder.snapOperations[snapOperationUuid][field] = decimalValue;

  for (const quotePriceUuid of state.quoteLines[quoteLineUuid]
    .quotePriceUuids) {
    onPartQuantityChange(state, {
      quoteLineUuid,
      quotePriceUuid,
      quantity:
        state.quoteLines[quoteLineUuid].quotePrices[quotePriceUuid].quantity,
      priceStrategyConfigs,
    });
  }
}
